import React, { useState, useEffect, useRef } from 'react';
import { 
    Card, 
    CardContent, 
    Typography, 
    Button, 
    Grid, 
    Box, 
    CircularProgress,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import * as appointmentActions from '../../actions/user/user.appointments';
import { Notification } from '../Atoms';

const AppointmentBooking = ({ storeId, storeName, onBookingSuccess, onBookingError }) => {
    const dispatch = useDispatch();
    
    // Redux state
    const { 
        availableSlots: reduxSlots, 
        timezones: reduxTimezones, 
        loading: reduxLoading,
        bookingLoading,
        bookingSuccess,
        bookingError,
        error 
    } = useSelector(state => state.appointments || {});
    
    const [selectedDate, setSelectedDate] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [userTimezone, setUserTimezone] = useState('Asia/Calcutta');
    const [notes, setNotes] = useState('');
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [timezones, setTimezones] = useState([]);
    
    // Ref to track the last processed error to prevent duplicate notifications
    const lastProcessedError = useRef(null);
    
    // Notification function following app pattern
    const sendNotification = (type, { title, description = "" }) => {
        Notification[type]({
            message: title,
            description,
        });
    };
    
    // Get user timezone from browser, default to India timezone
    useEffect(() => {

        const detectedTimezone = moment.tz.guess();

        // Default to India timezone if detection fails or returns invalid timezone
        const defaultTimezone = detectedTimezone && moment.tz.zone(detectedTimezone) ? detectedTimezone : 'Asia/Calcutta';
        setUserTimezone(defaultTimezone);
        
        // Set default timezones immediately for better UX
        setDefaultTimezones();
        
        // Then try to fetch timezones from API
        fetchTimezones();
    }, []);
    
    // Sync Redux timezones with local state
    useEffect(() => {
        if (reduxTimezones && reduxTimezones.length > 0) {
            setTimezones(reduxTimezones);
        }
    }, [reduxTimezones]);
    
    const fetchTimezones = () => {
        dispatch(appointmentActions.getTimezones());
    };
    
    const setDefaultTimezones = (forDate = new Date()) => {
        const defaultTimezones = [
            { value: 'UTC', label: 'UTC (Coordinated Universal Time)', offset: '+00:00' },
            { value: 'America/New_York', label: 'Eastern Time (USA)', offset: moment.tz(forDate, 'America/New_York').format('Z') },
            { value: 'America/Chicago', label: 'Central Time (USA)', offset: moment.tz(forDate, 'America/Chicago').format('Z') },
            { value: 'America/Denver', label: 'Mountain Time (USA)', offset: moment.tz(forDate, 'America/Denver').format('Z') },
            { value: 'America/Los_Angeles', label: 'Pacific Time (USA)', offset: moment.tz(forDate, 'America/Los_Angeles').format('Z') },
            { value: 'America/Toronto', label: 'Eastern Time (Canada)', offset: moment.tz(forDate, 'America/Toronto').format('Z') },
            { value: 'America/Winnipeg', label: 'Central Time (Canada)', offset: moment.tz(forDate, 'America/Winnipeg').format('Z') },
            { value: 'America/Edmonton', label: 'Mountain Time (Canada)', offset: moment.tz(forDate, 'America/Edmonton').format('Z') },
            { value: 'America/Vancouver', label: 'Pacific Time (Canada)', offset: moment.tz(forDate, 'America/Vancouver').format('Z') },
            { value: 'Europe/London', label: 'London (UK)', offset: moment.tz(forDate, 'Europe/London').format('Z') },
            { value: 'Europe/Paris', label: 'Central European Time', offset: moment.tz(forDate, 'Europe/Paris').format('Z') },
            { value: 'Asia/Calcutta', label: 'India Standard Time (IST)', offset: moment.tz(forDate, 'Asia/Calcutta').format('Z') },
            { value: 'Asia/Shanghai', label: 'China Standard Time', offset: moment.tz(forDate, 'Asia/Shanghai').format('Z') },
            { value: 'Asia/Tokyo', label: 'Japan Standard Time', offset: moment.tz(forDate, 'Asia/Tokyo').format('Z') },
            { value: 'Australia/Sydney', label: 'Australian Eastern Time', offset: moment.tz(forDate, 'Australia/Sydney').format('Z') },
            { value: 'Australia/Perth', label: 'Australian Western Time', offset: moment.tz(forDate, 'Australia/Perth').format('Z') },
            { value: 'Pacific/Auckland', label: 'New Zealand Time', offset: moment.tz(forDate, 'Pacific/Auckland').format('Z') }
        ];
        setTimezones(defaultTimezones);
    };
    
    const fetchAvailableSlots = (date) => {
        if (!date || !storeId) return;
        
        setLoadingSlots(true);
        const formattedDate = moment(date).format('YYYY-MM-DD');
        dispatch(appointmentActions.getAvailableSlots(storeId, formattedDate, userTimezone));
    };
    
    // Sync Redux slots with local state
    useEffect(() => {
        if (reduxSlots) {
            setAvailableSlots(reduxSlots);
            setLoadingSlots(false);
        }
    }, [reduxSlots]);

    // Re-fetch available slots when timezone changes
    useEffect(() => {
        if (selectedDate && storeId) {
            // Clear selected slot since times will be different in new timezone
            setSelectedSlot(null);
            fetchAvailableSlots(selectedDate);
        }
    }, [userTimezone]); // Re-run when userTimezone changes

    // Update timezone offsets when selected date changes (for DST accuracy)
    useEffect(() => {
        if (selectedDate) {
            setDefaultTimezones(selectedDate);
        }
    }, [selectedDate]); // Re-run when selectedDate changes
    
    // Handle booking success
    useEffect(() => {
        if (bookingSuccess) {
            sendNotification('success', {
                title: 'Video Call Booked Successfully!',
                description: 'You will receive a confirmation shortly'
            });
            setConfirmDialogOpen(false);
            setSelectedDate(null);
            setSelectedSlot(null);
            setNotes('');
            setAvailableSlots([]);
            
            if (onBookingSuccess) {
                // We need to get the appointment data from the Redux state
                // For now, we'll call the callback without data
                onBookingSuccess();
            }
            
            // Clear the success state
            dispatch(appointmentActions.clearAppointmentSuccess());
        }
    }, [bookingSuccess, dispatch, onBookingSuccess]);
    
    // Handle booking error
    useEffect(() => {
        if (bookingError && bookingError !== lastProcessedError.current) {
            // Update the ref to track this error as processed
            lastProcessedError.current = bookingError;
            
            // Close the modal/dialog first to ensure notification is visible
            setConfirmDialogOpen(false);
            
            // Call the error callback if provided (for parent components like ShopHeaderMini)
            if (onBookingError) {
                onBookingError(bookingError);
            }
            
            // Show notification with a slight delay to ensure modal is closed
            setTimeout(() => {
                // Extract error message from different possible paths in axios error object
                let errorMessage = 'Error booking appointment';
                
                if (bookingError.response?.data?.message) {
                    // API error message (most common case)
                    errorMessage = bookingError.response.data.message;
                } else if (bookingError.response?.data?.error) {
                    // Alternative API error format
                    errorMessage = bookingError.response.data.error;
                } else if (bookingError.message) {
                    // Network or other axios error
                    errorMessage = bookingError.message;
                } else if (typeof bookingError === 'string') {
                    // String error
                    errorMessage = bookingError;
                }
                
                sendNotification('error', {
                    title: 'Booking Failed',
                    description: errorMessage
                });
                
                // Clear the error state after showing notification
                dispatch(appointmentActions.clearAppointmentErrors());
            }, 300);
        }
    }, [bookingError, dispatch]);

    // Reset error tracking when error is cleared
    useEffect(() => {
        if (!bookingError) {
            lastProcessedError.current = null;
        }
    }, [bookingError]);
    
    const handleDateChange = (newDate) => {
        setSelectedDate(newDate);
        setSelectedSlot(null);
        if (newDate) {
            fetchAvailableSlots(newDate);
        } else {
            setAvailableSlots([]);
        }
    };
    
    const handleSlotSelect = (slot) => {
        setSelectedSlot(slot);
    };
    
    const handleBookAppointment = () => {
        if (!selectedSlot) {
            sendNotification('error', {
                title: 'No Time Slot Selected',
                description: 'Please select a time slot before booking your appointment.'
            });
            return;
        }
        setConfirmDialogOpen(true);
    };
    
    const confirmBooking = () => {
        if (!selectedSlot) return;
        
        const appointmentData = {
            store_id: storeId,
            appointment_date: moment(selectedDate).format('YYYY-MM-DD'),
            start_time: selectedSlot.start_time,
            end_time: selectedSlot.end_time,
            notes: notes.trim(),
            user_timezone: userTimezone
        };
        
        dispatch(appointmentActions.bookAppointment(appointmentData));
    };
    
    const formatSlotTime = (slot) => {
        return `${slot.display_time_user} (${slot.display_time_store} store time)`;
    };
    
    const isDateDisabled = (date) => {
        // Disable past dates
        return moment(date).isBefore(moment(), 'day');
    };
    
    return (
        <Box sx={{ position: 'relative' }}>
            {/* Sticky Book Appointment Button */}
            <Box 
                sx={{ 
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backgroundColor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    p: 2,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                {/* Selected slot information */}
                {selectedSlot && selectedDate && (
                    <Box mb={2} display="flex" flexDirection="column" alignItems="center">
                        <Typography variant="h6" color="primary" gutterBottom>
                            Selected Appointment
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            <strong>Date:</strong> {moment(selectedDate).format('MMMM Do, YYYY')}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            <strong>Time:</strong> {formatSlotTime(selectedSlot)}
                        </Typography>
                    </Box>
                )}

                <Box display="flex" justifyContent="center" alignItems="center">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleBookAppointment}
                        disabled={!selectedSlot || bookingLoading}
                        size="large"
                        sx={{ minWidth: 200 }}
                    >
                        Book Appointment
                    </Button>
                </Box>                
            </Box>

            {/* Main content area with proper spacing */}
            <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ px: 2 }}>
                    Select date:
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ px: 2 }}>
                    Select available time:
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ px: 2 }}>
                    Click "Book Appointment"
                </Typography>
                <Card>
                <CardContent>
                    <Box mb={3}>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Your Timezone</InputLabel>
                            <Select
                                value={userTimezone}
                                onChange={(e) => {
                                    const newTimezone = e.target.value;
                                    setUserTimezone(newTimezone);
                                    // Show loading state immediately for better UX
                                    if (selectedDate && storeId) {
                                        setLoadingSlots(true);
                                    }
                                }}
                                label="Your Timezone"
                            >
                                {timezones.map((tz) => (
                                    <MenuItem key={tz.value} value={tz.value}>
                                        {tz.label} ({tz.offset})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    
                    <Box mb={3}>
                        <Typography variant="subtitle1" gutterBottom>
                            Select Date
                        </Typography>
                        <DatePicker
                            selected={selectedDate ? selectedDate.toDate() : null}
                            onChange={(date) => handleDateChange(moment(date))}
                            minDate={new Date()}
                            maxDate={moment().add(30, 'days').toDate()} // Allow booking up to 30 days in advance
                            filterDate={(date) => !isDateDisabled(date)}
                            dateFormat="MMMM d, yyyy"
                            customInput={<TextField fullWidth />}
                            placeholderText="Select a date"
                        />
                    </Box>
                    
                    {selectedDate && (
                        <Box mb={3}>
                            <Typography variant="subtitle1" gutterBottom>
                                Available Time Slots for {moment(selectedDate).format('MMMM Do, YYYY')}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Times shown in your timezone: {timezones.find(tz => tz.value === userTimezone)?.label || userTimezone}
                            </Typography>
                            
                            {loadingSlots ? (
                                <Box display="flex" justifyContent="center" p={2}>
                                    <CircularProgress />
                                </Box>
                            ) : availableSlots.length > 0 ? (
                                <Box sx={{ maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
                                    <Grid container spacing={1}>
                                        {availableSlots.filter(slot => slot.available).map((slot, index) => (
                                            <Grid item xs={12} sm={6} md={4} key={index}>
                                                <Chip
                                                    label={formatSlotTime(slot)}
                                                    onClick={() => handleSlotSelect(slot)}
                                                    color={selectedSlot === slot ? 'primary' : 'default'}
                                                    variant={selectedSlot === slot ? 'filled' : 'outlined'}
                                                    clickable
                                                    sx={{ 
                                                        width: '100%', 
                                                        mb: 1,
                                                        height: 'auto',
                                                        '& .MuiChip-label': {
                                                            whiteSpace: 'normal',
                                                            padding: '8px'
                                                        }
                                                    }}
                                                />
                                                <Typography variant="caption" display="block" color="textSecondary">
                                                    {slot.available ? 'Available' : 'Not Available'}
                                                </Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            ) : (
                                <Alert severity="info">
                                    {availableSlots.length > 0 
                                        ? "All slots are booked for this date. Please select another date."
                                        : "No available slots for this date. Please select another date."
                                    }
                                </Alert>
                            )}
                        </Box>
                    )}
                    
                    {selectedSlot && (
                        <Box mb={3}>
                            <TextField
                                fullWidth
                                label="Notes (Optional)"
                                multiline
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Add any special requests or notes for your appointment..."
                                inputProps={{ maxLength: 500 }}
                                helperText={`${notes.length}/500 characters`}
                            />
                        </Box>
                    )}
                </CardContent>
                </Card>
            </Box>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Appointment Booking</DialogTitle>
                <DialogContent>
                    {selectedSlot && (
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                <strong>Store:</strong> {storeName}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>Date:</strong> {moment(selectedDate).format('MMMM Do, YYYY')}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>Time (Your timezone):</strong> {selectedSlot.display_time_user}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>Time (Store timezone):</strong> {selectedSlot.display_time_store}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>Duration:</strong> 30 minutes
                            </Typography>
                            {notes.trim() && (
                                <Typography variant="body1" gutterBottom>
                                    <strong>Notes:</strong> {notes.trim()}
                                </Typography>
                            )}
                            
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Please note: You can only have one active appointment per shop
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)} disabled={bookingLoading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmBooking} 
                        variant="contained" 
                        disabled={bookingLoading}
                        startIcon={bookingLoading ? <CircularProgress size={20} /> : null}
                    >
                        {bookingLoading ? 'Booking...' : 'Confirm Booking'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AppointmentBooking;
